/**
 * ImagePropertiesBar Component
 *
 * Bottom bar for editing image/design properties.
 * Shows when an image object is selected on the canvas.
 *
 * Features:
 * - Width/Height inputs with lock toggle (proportional scaling)
 * - Scale slider (50% - 200%)
 * - Fit/Fill buttons
 * - Quality indicator
 * - Delete button
 * - Print-Area tooltip on "cm" label
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FabricImage } from 'fabric';
import {
  Link,
  Unlink,
  Maximize,
  Maximize2,
  Trash2,
  Info,
  Palette,
} from 'lucide-react';
import { analyzeEmbroiderySource } from '@/api/embroidery';
import { useEmbroideryStore } from '@/stores/embroideryStore';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { logger } from '@/utils/logger';
import { useDesignStore } from '@/stores';
import { useSizeIndicator, usePrintContext } from '@/hooks';
import type { PrintContext } from '@/types/printContext';
import { useObjectOperations } from '@/hooks/useObjectOperations';
import { cn } from '@/lib/utils';
import {
  fitDesignToArea,
  fillDesignToArea,
  setDesignWidthCm,
  setDesignHeightCm,
} from '@/utils/designScaling';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { isSystemLayer } from '@/types/canvas';

/**
 * Quality indicator dots component. Exported for reuse by the Direct Sale
 * image action bar, which replaces this bar but keeps the quality feedback.
 */
export function QualityDots({
  level,
}: {
  level: 'poor' | 'medium' | 'good' | 'excellent';
}) {
  const filledDots = {
    poor: 1,
    medium: 2,
    good: 3,
    excellent: 4,
  }[level];

  const colors = {
    poor: 'bg-danger',
    medium: 'bg-warning',
    good: 'bg-success',
    excellent: 'bg-success',
  }[level];

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className={cn(
            'w-2 h-2 rounded-full',
            i <= filledDots ? colors : 'bg-paper-3'
          )}
        />
      ))}
    </div>
  );
}

/**
 * ImagePropertiesBar - Horizontal bar at the bottom for image editing
 */
export function ImagePropertiesBar() {
  // Print context - Single Source of Truth for all print area calculations;
  // without a calibrated template there is no area to size against.
  const printContext = usePrintContext();
  if (!printContext) return null;
  return <ImagePropertiesBarForContext printContext={printContext} />;
}

function ImagePropertiesBarForContext({
  printContext,
}: {
  printContext: PrintContext;
}) {
  const { t } = useTranslation();
  const { canvas, selectedObjects, saveToHistory, updateSizeInfo } =
    useDesignStore();
  const { deleteSelected } = useObjectOperations();
  const { sizeInfo } = useSizeIndicator();

  // State
  const [isProportionsLocked, setIsProportionsLocked] = useState(true);
  const [widthInput, setWidthInput] = useState('');
  const [heightInput, setHeightInput] = useState('');
  const [scalePercent, setScalePercent] = useState(100);
  const baseScaleRef = useRef<number>(1);
  const isUpdatingRef = useRef(false);

  // Get selected image
  const selectedImage = useMemo(() => {
    return selectedObjects.find(
      (obj) => obj.type === 'image' && !isSystemLayer(obj.name)
    ) as FabricImage | undefined;
  }, [selectedObjects]);

  // Print area config for scaling functions
  const printArea = useMemo(() => {
    return {
      x: printContext.offsetX,
      y: printContext.offsetY,
      width: printContext.widthPx,
      height: printContext.heightPx,
    };
  }, [printContext]);

  // Print area tooltip text - uses cm values from PrintContext
  const printAreaTooltip = useMemo(() => {
    return t('creator.image.printArea', {
      width: printContext.widthCm.toFixed(1),
      height: printContext.heightCm.toFixed(1),
    });
  }, [printContext.widthCm, printContext.heightCm, t]);

  // Sync inputs with size info
  useEffect(() => {
    if (!sizeInfo || isUpdatingRef.current) return;

    setWidthInput(sizeInfo.width.toFixed(2));
    setHeightInput(sizeInfo.height.toFixed(2));
  }, [sizeInfo]);

  // Store base scale when object is selected
  useEffect(() => {
    if (selectedImage) {
      baseScaleRef.current = selectedImage.scaleX || 1;
      setScalePercent(100);
    }
  }, [selectedImage]);

  // Handle width change
  const handleWidthChange = useCallback((value: string) => {
    setWidthInput(value);
  }, []);

  const handleWidthBlur = useCallback(() => {
    if (!selectedImage || !canvas) return;

    const newWidthCm = parseFloat(widthInput);
    if (isNaN(newWidthCm) || newWidthCm <= 0) {
      // Reset to current value
      if (sizeInfo) {
        setWidthInput(sizeInfo.width.toFixed(2));
      }
      return;
    }

    isUpdatingRef.current = true;
    setDesignWidthCm(
      selectedImage,
      newWidthCm,
      printContext.pxPerMm,
      isProportionsLocked
    );
    canvas.requestRenderAll();
    saveToHistory('resize');
    updateSizeInfo(); // Immediate size update

    // Update base scale
    baseScaleRef.current = selectedImage.scaleX || 1;
    setScalePercent(100);

    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 100);
  }, [
    selectedImage,
    canvas,
    widthInput,
    sizeInfo,
    isProportionsLocked,
    saveToHistory,
    updateSizeInfo,
    printContext.pxPerMm,
  ]);

  // Handle height change
  const handleHeightChange = useCallback((value: string) => {
    setHeightInput(value);
  }, []);

  const handleHeightBlur = useCallback(() => {
    if (!selectedImage || !canvas) return;

    const newHeightCm = parseFloat(heightInput);
    if (isNaN(newHeightCm) || newHeightCm <= 0) {
      // Reset to current value
      if (sizeInfo) {
        setHeightInput(sizeInfo.height.toFixed(2));
      }
      return;
    }

    isUpdatingRef.current = true;
    setDesignHeightCm(
      selectedImage,
      newHeightCm,
      printContext.pxPerMm,
      isProportionsLocked
    );
    canvas.requestRenderAll();
    saveToHistory('resize');
    updateSizeInfo(); // Immediate size update

    // Update base scale
    baseScaleRef.current = selectedImage.scaleX || 1;
    setScalePercent(100);

    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 100);
  }, [
    selectedImage,
    canvas,
    heightInput,
    sizeInfo,
    isProportionsLocked,
    saveToHistory,
    updateSizeInfo,
    printContext.pxPerMm,
  ]);

  // Handle scale slider change
  const handleScaleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!selectedImage || !canvas) return;

      const percent = parseInt(e.target.value, 10);
      setScalePercent(percent);

      const newScale = baseScaleRef.current * (percent / 100);
      selectedImage.scale(newScale);
      selectedImage.setCoords();
      canvas.requestRenderAll();
      updateSizeInfo(); // Immediate size update
    },
    [selectedImage, canvas, updateSizeInfo]
  );

  const handleScaleChangeEnd = useCallback(() => {
    saveToHistory('scale');
  }, [saveToHistory]);

  // Handle Fit button
  const handleFit = useCallback(() => {
    if (!selectedImage || !canvas) return;

    // The print area where the canvas draws it, anchor offset included; a
    // centred guess put the motif off the frame on off-centre positions.
    fitDesignToArea(selectedImage, printArea);
    canvas.requestRenderAll();
    saveToHistory('fit');
    updateSizeInfo(); // Immediate size update

    // Update base scale
    baseScaleRef.current = selectedImage.scaleX || 1;
    setScalePercent(100);
  }, [selectedImage, canvas, printArea, saveToHistory, updateSizeInfo]);

  // Handle Fill button
  const handleFill = useCallback(() => {
    if (!selectedImage || !canvas) return;

    // The print area where the canvas draws it, anchor offset included; a
    // centred guess put the motif off the frame on off-centre positions.
    fillDesignToArea(selectedImage, printArea);
    canvas.requestRenderAll();
    saveToHistory('fill');
    updateSizeInfo(); // Immediate size update

    // Update base scale
    baseScaleRef.current = selectedImage.scaleX || 1;
    setScalePercent(100);
  }, [selectedImage, canvas, printArea, saveToHistory, updateSizeInfo]);

  // Handle lock toggle
  const handleToggleLock = useCallback(() => {
    setIsProportionsLocked((prev) => !prev);
  }, []);

  // Handle delete
  const handleDelete = useCallback(() => {
    deleteSelected();
  }, [deleteSelected]);

  // ── Embroidery: reopen the thread mapping ────────────────────────────────
  //
  // Shown only for a motif that carries an embFileKey, so designs made before
  // this feature — and plain images on print products — are unaffected.
  const embFileKey = selectedImage?.embFileKey;

  const handleOpenThreadColors = useCallback(async () => {
    if (!embFileKey) return;
    const store = useEmbroideryStore.getState();

    // A palette is only in memory for a motif uploaded this session, and only
    // for THAT motif — keying on presence alone paired a second motif with the
    // first one's palette and source, so the modal edited colours the selected
    // object does not contain. After a reload the palette is re-derived from
    // the stored source; the extraction is deterministic, so its hex keys match
    // the recorded mapping's keys.
    if (store.palette.length > 0 && store.source.embFileKey === embFileKey) {
      store.openModal();
      return;
    }

    store.setProcessing(true);
    try {
      const palette = await analyzeEmbroiderySource(embFileKey);
      // The analysis can take a while and nothing stops the seller from
      // moving on meanwhile — another product, another selection. Seeding now
      // would open this artwork's colours over whatever is on the canvas, and
      // Apply would render them into it.
      const stillSelected = useDesignStore
        .getState()
        .selectedObjects.some((obj) => obj.embFileKey === embFileKey);
      if (!stillSelected) return;
      store.startMapping({
        palette,
        existingMapping: selectedImage?.confirmedMapping,
        objectUrl: null,
        embFileKey,
      });
    } catch (error) {
      logger.error(
        'ImagePropertiesBar',
        'Could not reopen thread colors:',
        error instanceof Error ? error.message : String(error)
      );
      // Silence here left the button doing nothing at all — and if the motif is
      // unconfirmed, that is also the seller's only route to publishing.
      toast.error(t('creator.image.threadColorsLoadFailed'));
    } finally {
      store.setProcessing(false);
    }
  }, [embFileKey, selectedImage, t]);

  // Handle Enter key in inputs
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, onBlur: () => void) => {
      if (e.key === 'Enter') {
        e.currentTarget.blur();
        onBlur();
      }
    },
    []
  );

  // Don't render if no image is selected
  if (!selectedImage) {
    return null;
  }

  return (
    <div className="absolute bottom-0 left-0 right-0 z-50 bg-paper border-t border-line ring-1 ring-line">
      <div className="h-14 px-4 flex items-center gap-4">
        {/* Width Input */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-mute w-4">B</span>
          <input
            type="text"
            value={widthInput}
            onChange={(e) => handleWidthChange(e.target.value)}
            onBlur={handleWidthBlur}
            onKeyDown={(e) => handleKeyDown(e, handleWidthBlur)}
            className="w-16 px-2 py-1 text-sm border border-line rounded focus:border-primary focus:outline-none text-center"
          />
        </div>

        {/* Lock Toggle */}
        <button
          onClick={handleToggleLock}
          className={cn(
            'p-1.5 rounded transition-colors',
            isProportionsLocked
              ? 'text-primary bg-primary-50'
              : 'text-mute hover:bg-paper-2'
          )}
          title={
            isProportionsLocked
              ? t('creator.image.unlockRatio')
              : t('creator.image.lockRatio')
          }
          aria-label={
            isProportionsLocked
              ? t('creator.image.unlockRatio')
              : t('creator.image.lockRatio')
          }
          aria-pressed={isProportionsLocked}
        >
          {isProportionsLocked ? (
            <Link className="w-4 h-4" />
          ) : (
            <Unlink className="w-4 h-4" />
          )}
        </button>

        {/* Height Input */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-mute w-4">H</span>
          <input
            type="text"
            value={heightInput}
            onChange={(e) => handleHeightChange(e.target.value)}
            onBlur={handleHeightBlur}
            onKeyDown={(e) => handleKeyDown(e, handleHeightBlur)}
            className="w-16 px-2 py-1 text-sm border border-line rounded focus:border-primary focus:outline-none text-center"
          />
        </div>

        {/* Unit */}
        <span className="text-sm text-mute">cm</span>

        {/* Print Area Info Icon with Tooltip */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="p-1 text-mute hover:text-ink-2 hover:bg-paper-2 rounded transition-colors"
              aria-label={t('creator.image.printAreaInfo')}
            >
              <Info className="w-4 h-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{printAreaTooltip}</p>
          </TooltipContent>
        </Tooltip>

        {/* Divider */}
        <div className="w-px h-6 bg-paper-3" />

        {/* Scale Slider */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-mute w-8">{scalePercent}%</span>
          <input
            type="range"
            min="50"
            max="200"
            value={scalePercent}
            onChange={handleScaleChange}
            onMouseUp={handleScaleChangeEnd}
            onTouchEnd={handleScaleChangeEnd}
            className="w-24 h-1 bg-paper-3 rounded-control appearance-none cursor-pointer accent-primary-500"
          />
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-paper-3" />

        {/* Fit/Fill Buttons */}
        <div className="flex items-center gap-1">
          <button
            onClick={handleFit}
            className="flex items-center gap-1 px-2 py-1 text-sm text-ink-2 hover:bg-paper-2 rounded transition-colors"
            title={t('creator.image.fitTitle')}
            aria-label={t('creator.image.fitTitle')}
          >
            <Maximize2 className="w-4 h-4" />
            <span>{t('creator.image.fit')}</span>
          </button>
          <button
            onClick={handleFill}
            className="flex items-center gap-1 px-2 py-1 text-sm text-ink-2 hover:bg-paper-2 rounded transition-colors"
            title={t('creator.image.fillTitle')}
            aria-label={t('creator.image.fillTitle')}
          >
            <Maximize className="w-4 h-4" />
            <span>{t('creator.image.fill')}</span>
          </button>
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-paper-3" />

        {/* Quality Indicator */}
        {sizeInfo?.quality && (
          <div className="flex items-center gap-2">
            <QualityDots level={sizeInfo.quality.level} />
            <span className="text-xs" style={{ color: sizeInfo.quality.color }}>
              {sizeInfo.qualityLabel}
            </span>
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Embroidery thread mapping */}
        {embFileKey && (
          <button
            onClick={handleOpenThreadColors}
            className="inline-flex items-center gap-1.5 px-2.5 py-2 text-ink-2 hover:text-ink hover:bg-paper-2 rounded-control transition text-sm"
            title={t('creator.image.threadColors')}
            aria-label={t('creator.image.editThreadColors')}
          >
            <Palette className="w-4 h-4" />
            <span className="hidden lg:inline">
              {t('creator.image.threadColors')}
            </span>
            {!selectedImage?.confirmedMapping && (
              <span
                className="w-1.5 h-1.5 rounded-full bg-warning"
                title={t('creator.image.threadColorsUnconfirmed')}
              />
            )}
          </button>
        )}

        {/* Delete Button */}
        <button
          onClick={handleDelete}
          className="p-2 text-mute hover:text-danger hover:bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] rounded-control transition"
          title={t('common.delete')}
          aria-label={t('creator.image.deleteSelected')}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
